import { ReactNode } from "react";
import { useViewportVisibilityForAnimations } from "~/components/1_atoms/useViewportVisibility";

interface StandardSectionProps {
  sectionClass?: string;
  paragraphClasses?: string;
  title?: string | { mobile: string; desktop: string } | ReactNode;
  paragraph: (string | ReactNode)[];
  decors?: ReactNode[];
  children?: ReactNode[];
}

function StandardSection({
  sectionClass = "",
  paragraphClasses = "",
  title,
  paragraph,
  decors,
  children,
}: StandardSectionProps) {
  const { ref, isVisible } = useViewportVisibilityForAnimations();

  // Calculate timing for sequential animations
  const titleDelay = 0;
  const paragraphStartDelay = 200;
  const paragraphStaggerDelay = 150;
  const childrenContainerDelay =
    paragraphStartDelay + paragraph.length * paragraphStaggerDelay + 100;
  const childStaggerDelay = 200;

  // Handle different title formats
  const renderTitle = () => {
    if (typeof title === "string") {
      return <span dangerouslySetInnerHTML={{ __html: title }} />;
    } else if (typeof title === "object" && title !== null && "mobile" in title) {
      return (
        <>
          <span
            className="lg:hidden"
            dangerouslySetInnerHTML={{ __html: title.mobile }}
          />
          <span
            className="hidden lg:inline"
            dangerouslySetInnerHTML={{ __html: title.desktop }}
          />
        </>
      );
    }
    return title;
  };

  return (
    <section ref={ref} className={`tg-section__wrapper ${sectionClass}`}>
      {decors && decors.length > 0 && (
        <div className="tg-section__grid--absolute">
          {decors.map((decor, index) => {
            // Extract key from React element if available, otherwise use a stable fallback
            const decorKey =
              typeof decor === "object" && decor !== null && "key" in decor && decor.key
                ? `wrapper-${decor.key}`
                : `decor-wrapper-${index}`;

            return (
              <div
                key={decorKey}
                className={`transition-all duration-700 ease-in-out ${
                  isVisible ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  transitionDelay: `${titleDelay + index * 100}ms`,
                }}
              >
                {decor}
              </div>
            );
          })}
        </div>
      )}
      <div className="tg-section__grid 3xl:min-h-[30vh] items-center py-12 lg:py-16">
        <div className="col-span-12 grid grid-cols-subgrid xl:text-center">
          <div className="col-span-12 grid grid-cols-subgrid">
            {title ? (
              <h3
                className={`tg-h4 col-span-12 transition-all duration-700 ease-in-out ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                }`}
                style={{
                  transitionDelay: `${titleDelay}ms`,
                }}
              >
                {renderTitle()}
              </h3>
            ) : null}
            <div
              className={`col-span-12 mt-6 space-y-2 lg:mt-8 lg:space-y-4 xl:col-start-3 xl:col-end-11 2xl:col-start-4 2xl:col-end-10 ${paragraphClasses}`}
            >
              {paragraph.map((text, index) => {
                // Generate a stable key for each paragraph item
                const paragraphKey =
                  typeof text === "string"
                    ? `paragraph-${text.slice(0, 50).replace(/\s+/g, "-")}-${index}`
                    : `paragraph-${index}`;

                return (
                  <p
                    key={paragraphKey}
                    className={`tg-body transition-all duration-700 ease-in-out ${
                      isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                    }`}
                    style={{
                      transitionDelay: `${paragraphStartDelay + index * paragraphStaggerDelay}ms`,
                    }}
                  >
                    {text}
                  </p>
                );
              })}
            </div>
          </div>
        </div>
        {children ? (
          <div
            className={`col-span-12 mt-12 flex flex-col gap-20 transition-all duration-700 ease-in-out lg:mt-16 xl:flex-row xl:justify-between xl:gap-35 ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
            }`}
            style={{
              transitionDelay: `${childrenContainerDelay}ms`,
            }}
          >
            {children.map((child, index) => {
              // Extract key from React element if available, otherwise use a stable fallback
              const childKey =
                typeof child === "object" && child !== null && "key" in child && child.key
                  ? `wrapper-${child.key}`
                  : `child-wrapper-${index}`;

              return (
                <div
                  key={childKey}
                  className={`w-full transition-all duration-700 ease-in-out ${
                    isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                  }`}
                  style={{
                    transitionDelay: `${childrenContainerDelay + 100 + index * childStaggerDelay}ms`,
                  }}
                >
                  {child}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default StandardSection;
